import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Moon, Play, X as XIcon } from 'lucide-react';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import { resolveCoverUrl } from '../utils/coverUrl';

// Hide the banner on auth flows + the player itself (where the listener can
// just press play). Shown everywhere else when a fresh sleep snapshot is on
// disk and the listener isn't already mid-track on it.
const HIDDEN_PREFIXES = [
  '/login',
  '/admin-login',
  '/writer-login',
  '/forgot-password',
  '/payment/callback',
  '/player/',
];

const formatPosition = (sec) => {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  if (m >= 60) {
    const h = Math.floor(m / 60);
    return `${h}:${String(m % 60).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
};

const friendlyWhen = (savedAt) => {
  const ms = Date.now() - savedAt;
  const mins = Math.floor(ms / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 12) return `${hrs}h ago`;
  return 'last night';
};

/**
 * SleepResumeBanner — surfaces a one-tap resume CTA after the listener fell
 * asleep mid-track. Reads the snapshot written by AudioPlayerContext when
 * the sleep timer (or end-of-track sleep) fires.
 *
 * Behavior:
 *  • Only visible if a snapshot exists, is < 24h old, and the listener isn't
 *    already playing the same track.
 *  • Resume → load track at saved position and play.
 *  • Dismiss → clear snapshot.
 *  • Auto-fades after 30s of being on-screen so it doesn't camp forever.
 */
const SleepResumeBanner = () => {
  const { track, load, play, getSleepSnapshot, dismissSleepSnapshot, sleepResumeBumpAt } =
    useAudioPlayer();
  const location = useLocation();
  const [autoHidden, setAutoHidden] = useState(false);

  const snapshot = useMemo(
    () => getSleepSnapshot(),
    // re-evaluate on bump (new snapshot written) or route change (so it
    // re-appears if the listener just navigated to dashboard/library)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sleepResumeBumpAt, location.pathname, getSleepSnapshot]
  );

  // 30s auto-fade so it doesn't loiter. Resets when a new snapshot arrives.
  const snapshotSavedAt = snapshot?.savedAt;
  useEffect(() => {
    setAutoHidden(false);
    if (snapshotSavedAt == null) return undefined;
    const id = setTimeout(() => setAutoHidden(true), 30000);
    return () => clearTimeout(id);
  }, [snapshotSavedAt]);

  if (!snapshot) return null;
  if (autoHidden) return null;
  if (HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p))) return null;
  // Already playing this track — listener resumed manually, no need to nag.
  if (track?.id === snapshot.track.id) return null;

  const cover = resolveCoverUrl(snapshot.track.cover);
  const when = friendlyWhen(snapshot.savedAt);
  const reasonLabel =
    snapshot.reason === 'end-of-track' ? 'finished while you slept' : `paused ${when}`;

  const handleResume = () => {
    load({ ...snapshot.track, last_position: snapshot.position });
    // Browsers gate play() on a user gesture; the click that reached us IS
    // that gesture, so the play call should succeed.
    setTimeout(() => {
      play();
    }, 80);
    dismissSleepSnapshot();
  };

  return (
    <div
      className="fixed left-0 right-0 z-[55] px-3 sm:px-4 pointer-events-none"
      style={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      data-testid="sleep-resume-banner"
    >
      <div className="pointer-events-auto mx-auto max-w-2xl rounded-2xl bg-gradient-to-r from-purple-900/95 via-indigo-900/95 to-slate-900/95 backdrop-blur-xl border border-purple-500/40 shadow-2xl shadow-purple-950/40 overflow-hidden">
        <div className="flex items-center gap-3 px-3 py-2.5">
          <div className="relative h-12 w-12 rounded-lg overflow-hidden bg-slate-800 ring-1 ring-purple-400/40 flex-shrink-0">
            {cover ? (
              <img
                src={cover}
                alt={snapshot.track.title || 'Resume'}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-purple-700 to-indigo-700">
                <Moon className="w-5 h-5 text-purple-100" />
              </div>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-[0.2em] text-purple-300 font-semibold flex items-center gap-1">
              <Moon className="w-3 h-3" />
              Pick up where you slept
            </p>
            <p
              className="text-sm font-semibold text-white truncate"
              data-testid="sleep-resume-title"
            >
              {snapshot.track.title || 'Untitled'}
            </p>
            <p className="text-[11px] text-purple-200 truncate font-mono tabular-nums">
              {snapshot.reason === 'end-of-track'
                ? reasonLabel
                : `${reasonLabel} at ${formatPosition(snapshot.position)}`}
              {snapshot.track.series_title ? ` · ${snapshot.track.series_title}` : ''}
            </p>
          </div>

          <button
            type="button"
            onClick={handleResume}
            className="h-10 px-3 sm:px-4 rounded-full bg-purple-500 hover:bg-purple-400 flex items-center gap-1.5 text-sm font-semibold text-white shadow-lg shadow-purple-900/40 transition-colors flex-shrink-0"
            data-testid="sleep-resume-button"
            aria-label="Resume playback"
          >
            <Play className="w-4 h-4" />
            <span className="hidden sm:inline">Resume</span>
          </button>

          <button
            type="button"
            onClick={dismissSleepSnapshot}
            className="h-9 w-9 rounded-full text-purple-200 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors flex-shrink-0"
            data-testid="sleep-resume-dismiss"
            aria-label="Dismiss"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SleepResumeBanner;
